/* Billing — ORIO Pro subscription page (dark Apple Fitness style).
   Shows:
     - Header with current plan status (Free · Trial · Pro Attivo)
     - Monthly/Yearly toggle (€4,99/mo · €40/yr)
     - Side-by-side FREE vs PRO comparison cards
     - Primary CTA: "Inizia 30gg gratis" or "Abbonati €4,99"
     - "Il tuo piano" section (only for Pro): cancel one-tap + invoices + Stripe portal link
*/
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Sparkles, Check, X, Loader2, Crown, Shield, HeartHandshake,
  MapPin, PawPrint, Users, FileText, Route, Radar, MessageCircle, Zap,
  BookOpen, GraduationCap, ChevronRight, AlertCircle, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";

/* Feature list — the single source of truth for what's Free vs Pro.
   Kept in this file so a future refactor moves it once. */
const FREE_FEATURES = [
  { icon: PawPrint,      text: "1 passaporto cane" },
  { icon: FileText,      text: "Fino a 3 documenti nel cassetto" },
  { icon: MapPin,        text: "Mappa e segnalazioni complete" },
  { icon: Shield,        text: "Veterinario one-tap" },
  { icon: HeartHandshake,text: "Missioni pubbliche (visualizza)" },
  { icon: MessageCircle, text: "2 storie al giorno" },
  { icon: Sparkles,      text: "ORIO AI · 3 domande al giorno" },
];

const PRO_FEATURES = [
  { icon: Route,          text: "ORIO Walk illimitato + achievements" },
  { icon: HeartHandshake, text: "Match + Park Room + chat" },
  { icon: Shield,         text: "SOS Emergenza · raggio 80 km" },
  { icon: FileText,       text: "Cassetto illimitato + export PDF" },
  { icon: PawPrint,       text: "Cani illimitati" },
  { icon: Radar,          text: "ORIO Radar completo" },
  { icon: Eye,            text: "Visibilità mappa in tempo reale" },
  { icon: Sparkles,       text: "ORIO AI illimitato" },
  { icon: Zap,            text: "Stats avanzate + streak" },
  { icon: MessageCircle,  text: "Storie illimitate + diario completo" },
  { icon: GraduationCap,  text: "Corsi ORIO (in arrivo)" },
];

export default function Billing() {
  const { user } = useAuth();
  const {
    startTrial, canStartTrial, startPaidCheckout,
    cancelSubscription, resumeSubscription,
  } = useSubscription();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("yearly"); // Best-value default
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/billing/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, [user]);

  // Paint the page's html+body dark while the Billing page is mounted so no
  // white AppShell peeks out below the scrollable dark gradient. Cleans up
  // on unmount so the rest of the app keeps its warm cream aesthetic.
  // Also toggle `orio-billing-active` on <body> so index.css can neutralize
  // the AppShell's 140px MobileNav spacer (no nav on this page → no gap).
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.background;
    const prevBody = body.style.background;
    html.style.background = "#050403";
    body.style.background = "#050403";
    body.classList.add("orio-billing-active");
    return () => {
      html.style.background = prevHtml;
      body.style.background = prevBody;
      body.classList.remove("orio-billing-active");
    };
  }, []);

  const isPro = !!user?.is_pro;
  const inTrial = summary?.subscription_status === "trialing" || user?.in_trial;
  const cancelScheduled = !!user?.cancel_at_period_end || !!summary?.cancel_at_period_end;
  const trialUsed = !!summary?.trial_used;

  const daysLeft = useMemo(() => {
    const raw = summary?.trial_ends_at || summary?.subscription_ends_at
      || user?.trial_ends_at || user?.subscription_ends_at;
    if (!raw) return null;
    const d = Math.ceil((new Date(raw).getTime() - Date.now()) / 86400000);
    return d > 0 ? d : 0;
  }, [summary, user]);

  const primaryLabel = useMemo(() => {
    if (isPro) return null;
    if (canStartTrial && !trialUsed) return "Inizia 30 giorni gratis";
    return plan === "yearly" ? "Abbonati · €40/anno" : "Abbonati · €4,99/mese";
  }, [isPro, canStartTrial, trialUsed, plan]);

  const primaryAction = async () => {
    setBusy(true);
    try {
      if (canStartTrial && !trialUsed && !isPro) {
        const r = await startTrial();
        if (r) navigate("/app", { replace: true });
        return;
      }
      await startPaidCheckout(plan);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Errore");
    } finally { setBusy(false); }
  };

  const openStripePortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portale non disponibile");
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await cancelSubscription();
      setConfirmCancel(false);
    } finally { setBusy(false); }
  };

  const doResume = async () => {
    setBusy(true);
    try { await resumeSubscription(); } finally { setBusy(false); }
  };

  return (
    <div className="relative text-white pb-8" data-testid="billing-page"
      style={{
        background:
          "radial-gradient(140% 60% at 0% 0%, rgba(255,122,26,0.20) 0%, rgba(255,122,26,0) 55%), " +
          "radial-gradient(120% 60% at 100% 100%, rgba(52,211,153,0.14) 0%, rgba(52,211,153,0) 55%), " +
          "linear-gradient(180deg, #14100C 0%, #0B0806 55%, #050403 100%)",
      }}>
      {/* Top nav */}
      <div className="max-w-3xl mx-auto px-4 pt-8 pb-2">
        <Link to={user ? "/app" : "/"} data-testid="billing-back"
          className="text-[12px] text-white/55 hover:text-white inline-flex items-center gap-1.5">
          <ArrowLeft className="h-3.5 w-3.5" /> Indietro
        </Link>
      </div>

      {/* Status hero */}
      <div className="max-w-3xl mx-auto px-4 pt-2">
        <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/55 inline-flex items-center gap-1.5">
          <Crown className="h-3 w-3" style={{ color: "#F4B83C" }} /> ORIO Pro
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-1">
          {isPro
            ? <>Il tuo piano è<br /><span className="orio-grad-text">attivo.</span></>
            : <>Scegli il piano<br /><span className="orio-grad-text">che fa per te.</span></>}
        </h1>
        <p className="mt-3 text-[13.5px] text-white/60 leading-relaxed max-w-md">
          {isPro
            ? cancelScheduled
              ? `Disdetta programmata · avrai accesso Pro ancora ${daysLeft ?? "—"} giorni.`
              : inTrial
                ? `Sei in prova · restano ${daysLeft ?? "—"} giorni. Aggiungi una carta quando vuoi.`
                : "Rinnovo automatico attivo. Puoi disdire quando vuoi in un tap."
            : trialUsed
              ? "Hai già utilizzato la prova gratuita. Abbonati senza vincoli, disdici quando vuoi."
              : "Prova 30 giorni gratis · nessuna carta richiesta all'attivazione."}
        </p>
      </div>

      {/* Plan toggle — always visible so ANY user (Free, trial, Pro) can
          compare monthly vs yearly pricing at a glance. Pro users see it too. */}
      <div className="max-w-3xl mx-auto px-4 mt-6">
        <div className="inline-flex rounded-full border border-white/10 p-1"
             style={{ background: "rgba(255,255,255,0.04)", backdropFilter: "blur(10px)" }}
             data-testid="billing-plan-toggle">
          <ToggleBtn active={plan === "monthly"} onClick={() => setPlan("monthly")} testid="plan-monthly">
            Mensile
          </ToggleBtn>
          <ToggleBtn active={plan === "yearly"} onClick={() => setPlan("yearly")} testid="plan-yearly">
            Annuale
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                  style={{ background: "linear-gradient(135deg,#34D399,#2DBA60)", color: "#0A2A18" }}>
              -33%
            </span>
          </ToggleBtn>
        </div>
      </div>

      {/* Comparison cards */}
      <div className="max-w-3xl mx-auto px-4 mt-4 pb-4 grid md:grid-cols-2 gap-3">
        <PlanCard
          title="Free"
          price="€0"
          period=""
          tagline="Per iniziare a usare ORIO"
          features={FREE_FEATURES}
          highlight={false}
          currentPlan={!isPro}
          data-testid="plan-card-free"
        />
        <PlanCard
          title="ORIO Pro"
          price={plan === "yearly" ? "€40" : "€4,99"}
          period={plan === "yearly" ? "/anno" : "/mese"}
          subline={plan === "yearly" ? "≈ €3,33/mese · risparmi €20" : "Disdici quando vuoi"}
          tagline="Tutto ORIO, senza limiti"
          features={PRO_FEATURES}
          highlight
          currentPlan={isPro}
          data-testid="plan-card-pro"
        />
      </div>

      {/* Primary CTA */}
      {!isPro && primaryLabel && (
        <div className="max-w-3xl mx-auto px-4 mt-2">
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={primaryAction}
            disabled={busy}
            data-testid="billing-primary-cta"
            className="w-full py-4 rounded-full text-white font-semibold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{
              background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
              boxShadow: "0 24px 44px -14px rgba(233,69,69,0.75), 0 0 0 6px rgba(255,255,255,0.04)",
            }}
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {primaryLabel}
          </motion.button>
          <p className="mt-3 text-center text-[11px] text-white/45 leading-relaxed">
            🔒 Pagamento sicuro con Stripe · disdici in un tap · nessun costo nascosto
          </p>
        </div>
      )}

      {/* "Il tuo piano" — management section (Pro users only) */}
      {isPro && (
        <div className="max-w-3xl mx-auto px-4 mt-2 space-y-2 pb-4">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/55 mt-6 mb-2">
            Il tuo piano
          </div>

          {cancelScheduled ? (
            <ManageRow
              icon={AlertCircle}
              accent="#F4B83C"
              title="Rinnovo automatico disattivato"
              sub={`Manterrai Pro fino al ${fmtDate(summary?.subscription_ends_at || user?.subscription_ends_at)}.`}
              ctaLabel="Riattiva rinnovo"
              ctaTestid="resume-sub"
              onClick={doResume}
              busy={busy}
            />
          ) : (
            <ManageRow
              icon={X}
              accent="#E94545"
              title="Disdici l'abbonamento"
              sub="Manterrai Pro fino alla scadenza. Nessuna penale, nessuna domanda."
              ctaLabel="Disdici"
              ctaTestid="cancel-sub"
              onClick={() => setConfirmCancel(true)}
              busy={busy}
            />
          )}

          <ManageRow
            icon={FileText}
            accent="#5B9EE0"
            title="Metodo di pagamento e fatture"
            sub="Gestisci carta, ricevute e dati fatturazione tramite Stripe."
            ctaLabel="Apri portale"
            ctaTestid="stripe-portal"
            onClick={openStripePortal}
            busy={busy}
          />

          {/* Invoices list */}
          {summary?.invoices?.length > 0 && (
            <div className="rounded-2xl border p-4"
                 style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.08)", backdropFilter: "blur(10px)" }}>
              <div className="text-[10.5px] uppercase tracking-[0.20em] text-white/55 mb-2">
                Ultime fatture
              </div>
              <div className="space-y-1">
                {summary.invoices.slice(0, 6).map((i) => (
                  <a key={i.id} href={i.pdf || i.hosted_invoice_url} target="_blank" rel="noreferrer"
                     className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 hover:bg-white/[0.03] rounded-lg px-2 -mx-2"
                     data-testid={`invoice-${i.id}`}>
                    <div className="text-[12.5px] text-white/80">{fmtDate(i.created)}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-[12.5px] tabular-nums font-semibold text-white">
                        {i.amount_paid.toFixed(2)} €
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-white/40" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legal footer */}
      <div className="max-w-3xl mx-auto px-4 pb-6">
        <p className="text-[10.5px] text-white/40 leading-relaxed">
          Servizio erogato da Vixel (vixelgraphic.com · P.IVA 18531031005) tramite Stripe.
          <br />
          <Link to="/terms" className="underline underline-offset-2 hover:text-white/70">Termini</Link>
          {" · "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-white/70">Privacy</Link>
        </p>
      </div>

      {/* Cancel confirm sheet */}
      <AnimatePresence>
        {confirmCancel && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1800] flex items-end sm:items-center justify-center px-3 pb-4"
            onClick={() => setConfirmCancel(false)}
            data-testid="cancel-confirm-sheet"
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <motion.div
              initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
              transition={{ type: "spring", damping: 26, stiffness: 280 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full sm:max-w-md rounded-t-[28px] sm:rounded-[28px] p-6 border text-white"
              style={{
                background: "linear-gradient(180deg, #14100C 0%, #0B0806 100%)",
                borderColor: "rgba(255,255,255,0.12)",
              }}
            >
              <div className="mx-auto h-1 w-10 rounded-full bg-white/20 mb-4" />
              <h3 className="font-display text-2xl leading-tight">Sicuro di disdire?</h3>
              <p className="mt-2 text-[13px] text-white/60 leading-relaxed">
                Manterrai l'accesso Pro fino alla scadenza {daysLeft ? `(${daysLeft} giorni)` : ""}.
                Poi tornerai al piano gratuito.
              </p>
              <div className="mt-5 flex gap-2">
                <button
                  onClick={() => setConfirmCancel(false)}
                  className="flex-1 px-5 py-3.5 rounded-full font-semibold text-[14px] border"
                  style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.12)", color: "#fff" }}
                  data-testid="cancel-abort"
                >
                  Torna indietro
                </button>
                <button
                  onClick={doCancel}
                  disabled={busy}
                  className="flex-1 px-5 py-3.5 rounded-full font-semibold text-[14px] text-white disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg,#E94545,#B32020)", boxShadow: "0 16px 32px -12px rgba(233,69,69,0.55)" }}
                  data-testid="cancel-confirm"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Disdici"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- pieces ---------- */

function ToggleBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="relative px-4 sm:px-5 py-2 rounded-full text-[13px] font-semibold inline-flex items-center transition-colors"
      style={{
        color: active ? "#0F0F0F" : "rgba(255,255,255,0.75)",
        background: active
          ? "linear-gradient(135deg,#FFB020,#F4B83C)"
          : "transparent",
        boxShadow: active ? "0 6px 16px -8px rgba(255,176,32,0.55)" : "none",
      }}
    >
      {children}
    </button>
  );
}

function PlanCard({ title, price, period, subline, tagline, features, highlight, currentPlan, ...rest }) {
  return (
    <div
      className="rounded-3xl border p-5 relative overflow-hidden"
      style={{
        background: highlight
          ? "linear-gradient(155deg, rgba(255,122,26,0.20) 0%, rgba(244,184,60,0.08) 55%, rgba(11,8,6,0.6) 100%)"
          : "rgba(255,255,255,0.04)",
        borderColor: highlight ? "rgba(255,122,26,0.45)" : "rgba(255,255,255,0.10)",
        backdropFilter: "blur(12px)",
        boxShadow: highlight ? "0 24px 48px -20px rgba(255,122,26,0.35)" : "none",
      }}
      {...rest}
    >
      {highlight && (
        <div aria-hidden className="absolute -top-24 -right-16 h-56 w-56 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,176,32,0.45) 0%, transparent 65%)" }} />
      )}
      <div className="relative flex items-baseline justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {highlight && <Crown className="h-3.5 w-3.5" style={{ color: "#F4B83C" }} />}
          <div className="font-display text-lg tracking-tight text-white">{title}</div>
        </div>
        {currentPlan && (
          <span className="text-[9.5px] font-bold uppercase tracking-[0.16em] px-2 py-0.5 rounded-full"
                style={{ background: "rgba(52,211,153,0.20)", color: "#34D399", border: "1px solid rgba(52,211,153,0.40)" }}>
            Attivo
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-display text-4xl tracking-tight tabular-nums text-white">{price}</span>
        {period && <span className="text-sm text-white/55">{period}</span>}
      </div>
      {subline && <div className="text-[11px] text-[#F4B83C] mt-0.5 font-medium">{subline}</div>}
      <div className="text-[12px] text-white/55 mt-1.5">{tagline}</div>

      <div className="mt-4 space-y-2">
        {features.map((f, idx) => (
          <div key={idx} className="flex items-start gap-2 text-[13px]">
            <span className="h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-px"
              style={{
                background: highlight ? "rgba(52,211,153,0.20)" : "rgba(255,255,255,0.06)",
                border: highlight ? "1px solid rgba(52,211,153,0.40)" : "1px solid rgba(255,255,255,0.10)",
              }}>
              <Check className="h-3 w-3" style={{ color: highlight ? "#34D399" : "rgba(255,255,255,0.55)" }} />
            </span>
            <span className="text-white/85">{f.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ManageRow({ icon: Icon, accent, title, sub, ctaLabel, ctaTestid, onClick, busy }) {
  return (
    <div className="rounded-2xl border p-4 flex items-start gap-3"
         style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.08)", backdropFilter: "blur(10px)" }}>
      <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
           style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}55` }}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-white leading-tight">{title}</div>
        <div className="text-[11.5px] text-white/55 mt-0.5 leading-snug">{sub}</div>
      </div>
      <button onClick={onClick} disabled={busy} data-testid={ctaTestid}
        className="shrink-0 px-3.5 py-2 rounded-full text-[12px] font-semibold border transition-colors disabled:opacity-50"
        style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.12)", color: "#fff" }}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : ctaLabel}
      </button>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return ""; }
}
